import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  MessageCircle, Heart, Eye, Share2, ArrowRight, Zap, Shield, Smartphone,
  TrendingUp, Users, Zap as ZapIcon, Award, Rocket, CheckCircle, Star, Target
} from 'lucide-react';
import { usePageSEO } from '../hooks/useSEO';
import { useAppStore } from '../store/appStore';
import { BannerAd, ResponsiveAd } from '../components/GoogleAdSense';

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useAppStore();
  const [hoveredFeature, setHoveredFeature] = useState(null);
  
  usePageSEO('home', language);

  const features = [
    {
      id: 'comments',
      icon: MessageCircle,
      title: t('services.comments'),
      description: t('home.featureCommentsDesc'),
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      stats: t('home.featureCommentsStat'),
    },
    {
      id: 'likes',
      icon: Heart,
      title: t('services.likes'),
      description: t('home.featureLikesDesc'),
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      stats: t('home.featureLikesStat'),
    },
    {
      id: 'views',
      icon: Eye,
      title: t('services.views'),
      description: t('home.featureViewsDesc'),
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      stats: t('home.featureViewsStat'),
    },
    {
      id: 'shares',
      icon: Share2,
      title: t('services.shares'),
      description: t('home.featureSharesDesc'),
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      stats: t('home.featureSharesStat'),
    },
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: t('home.benefitGrowthTitle'),
      description: t('home.benefitGrowthDesc'),
      number: '300%',
    },
    {
      icon: Shield,
      title: t('home.benefitSecureTitle'),
      description: t('home.benefitSecureDesc'),
      number: '100%',
    },
    {
      icon: Smartphone,
      title: t('home.benefitEasyTitle'),
      description: t('home.benefitEasyDesc'),
      number: '5*',
    },
    {
      icon: Zap,
      title: t('home.benefitFastTitle'),
      description: t('home.benefitFastDesc'),
      number: '24/7',
    },
  ];

  const testimonials = [
    {
      name: 'Ahmed M.',
      role: t('home.testimonial1Role'),
      text: t('home.testimonial1Text'),
      rating: 5,
      image: '👨‍💼',
    },
    {
      name: 'Fatima A.',
      role: t('home.testimonial2Role'),
      text: t('home.testimonial2Text'),
      rating: 5,
      image: '👩‍💼',
    },
    {
      name: 'Mahmoud H.',
      role: t('home.testimonial3Role'),
      text: t('home.testimonial3Text'),
      rating: 5,
      image: '📸',
    },
  ];

  const stats = [
    { number: '10K+', label: t('home.statClients'), icon: Users },
    { number: '1M+', label: t('home.statPosts'), icon: Target },
    { number: '24/7', label: t('home.statSupport'), icon: Award },
    { number: '99.9%', label: t('home.statSatisfaction'), icon: Star },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-light via-light to-light/95">
      {/* ==================== HERO SECTION ==================== */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-maroon/10 to-primary/10 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-primary/10 to-accent/10 rounded-full blur-3xl -z-10"></div>

        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-slide-up">
              <div className="space-y-4">
                <div className="inline-block px-4 py-2 bg-maroon/10 border border-maroon/30 rounded-full">
                  <span className="text-maroon font-semibold text-sm">{t('home.badge')}</span>
                </div>
                
                <h1 className="text-6xl lg:text-7xl font-black leading-tight">
                  <span className="gradient-text">{t('home.heroTitleLine1')}</span>
                  <br />
                  <span className="text-maroon">{t('home.heroTitleLine2')}</span>
                </h1>
                
                <p className="text-xl text-dark/70 leading-relaxed max-w-lg">
                  {t('home.heroSubtitle')}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="btn-primary flex items-center justify-center gap-2 py-4 px-8 rounded-lg font-bold text-lg"
                >
                  {t('home.ctaPrimary')}
                  <ArrowRight size={20} />
                </button>
                <button className="btn-secondary flex items-center justify-center gap-2 py-4 px-8 rounded-lg font-bold text-lg">
                  {t('home.watchVideo')}
                  <span>▶️</span>
                </button>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-4 pt-8 border-t-2 border-primary/30">
                {stats.slice(0, 3).map((stat, idx) => {
                  const Icon = stat.icon;
                  return (
                    <div key={idx} className="text-center">
                      <div className="flex items-center justify-center mb-2">
                        <Icon size={20} className="text-maroon mr-2" />
                        <div className="text-2xl font-bold text-maroon">{stat.number}</div>
                      </div>
                      <p className="text-dark/70 text-sm">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative animate-float">
              <div className="absolute inset-0 bg-gradient-to-r from-maroon to-primary rounded-3xl blur-2xl opacity-20"></div>
              
              <div className="relative bg-gradient-to-br from-light to-light/95 border-3 border-primary/30 rounded-3xl p-8 shadow-2xl">
                {/* Floating cards */}
                <div className="space-y-4">
                  {[
                    { emoji: '💬', text: t('services.comments'), color: 'bg-blue-50' },
                    { emoji: '❤️', text: t('services.likes'), color: 'bg-red-50' },
                    { emoji: '👁️', text: t('services.views'), color: 'bg-purple-50' },
                  ].map((item, idx) => (
                    <div 
                      key={idx}
                      className={`${item.color} border-2 border-primary/30 rounded-2xl p-4 flex items-center gap-3 animate-slide-in`}
                      style={{ animationDelay: `${idx * 0.1}s` }}
                    >
                      <span className="text-3xl">{item.emoji}</span>
                      <div>
                        <p className="font-bold text-maroon">{item.text}</p>
                        <p className="text-sm text-dark/50">{t('home.instantSecure')}</p>
                      </div>
                      <div className="ml-auto">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-maroon to-primary flex items-center justify-center text-white text-sm font-bold">
                          ✓
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FEATURES SECTION ==================== */}
      <section id="services" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-light to-light/95">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 animate-slide-up">
            <h2 className="text-5xl font-black text-maroon mb-4">
              {t('home.featuresTitle')}
            </h2>
            <p className="text-xl text-dark/70 max-w-2xl mx-auto">
              {t('home.featuresSubtitle')}
            </p>
            <div className="divider max-w-xs mx-auto mt-8"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              const isHovered = hoveredFeature === feature.id;
              
              return (
                <div
                  key={idx}
                  onMouseEnter={() => setHoveredFeature(feature.id)}
                  onMouseLeave={() => setHoveredFeature(null)}
                  className={`card group cursor-pointer transform transition-all duration-300 ${
                    isHovered ? 'scale-105 shadow-2xl' : ''
                  }`}
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className={`${feature.bgColor} rounded-2xl p-6 mb-4 transition-all duration-300 ${
                    isHovered ? 'scale-110' : ''
                  }`}>
                    <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center shadow-lg group-hover:animate-bounce`}>
                      <Icon size={32} className="text-light" />
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-maroon mb-3">{feature.title}</h3>
                  <p className="text-dark/70 mb-4">{feature.description}</p>
                  
                  <div className="pt-4 border-t-2 border-primary/30">
                    <p className="text-sm font-semibold text-maroon flex items-center gap-2">
                      <TrendingUp size={16} />
                      {feature.stats}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== AD SPACE 1 ==================== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ResponsiveAd adSlot="1234567890" />
      </div>

      {/* ==================== BENEFITS SECTION ==================== */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-maroon to-primary relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-light/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20 animate-slide-up">
            <h2 className="text-5xl font-black text-light mb-4">
              {t('home.whyTitle')}
            </h2>
            <p className="text-xl text-light/90 max-w-2xl mx-auto">
              {t('home.whySubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={idx}
                  className="bg-light/10 backdrop-blur-md border border-light/20 rounded-2xl p-8 text-center hover:bg-light/20 transition-all duration-300 transform hover:scale-105 animate-scale-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="w-16 h-16 bg-light rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon size={32} className="text-maroon" />
                  </div>
                  <h3 className="text-xl font-bold text-light mb-2">{benefit.title}</h3>
                  <p className="text-light/80 mb-4">{benefit.description}</p>
                  <div className="text-3xl font-black text-accent">{benefit.number}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== AD SPACE 2 ==================== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <BannerAd adSlot="1234567891" />
      </div>

      {/* ==================== TESTIMONIALS SECTION ==================== */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20 animate-slide-up">
            <h2 className="text-5xl font-black text-maroon mb-4">
              {t('home.testimonialsTitle')}
            </h2>
            <p className="text-xl text-dark/70 max-w-2xl mx-auto">
              {t('home.testimonialsSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="card-elevated p-8 rounded-2xl animate-slide-up"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-5xl">{testimonial.image}</div>
                  <div>
                    <h4 className="font-bold text-maroon">{testimonial.name}</h4>
                    <p className="text-sm text-dark/60">{testimonial.role}</p>
                  </div>
                </div>
                
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                <p className="text-dark/70 italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== AD SPACE 3 ==================== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ResponsiveAd adSlot="1234567892" />
      </div>

      {/* ==================== CTA SECTION ==================== */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-maroon via-primary to-accent relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-light/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-light/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10 animate-slide-up">
          <h2 className="text-5xl font-black text-light mb-6">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-xl text-light/90 mb-8">
            {t('home.ctaSubtitle')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-light hover:bg-light/90 text-maroon font-bold py-4 px-12 rounded-lg transition transform hover:scale-105 shadow-2xl flex items-center justify-center gap-2"
            >
              <Rocket size={20} />
              {t('home.ctaButton')}
            </button>
            <button className="border-2 border-light hover:bg-light/10 text-light font-bold py-4 px-12 rounded-lg transition transform hover:scale-105">
              {t('home.ctaContactButton')}
            </button>
          </div>

          <div className="mt-12 flex justify-center gap-8 flex-wrap">
            {[
              { icon: '✓', text: t('home.ctaNoCard') },
              { icon: '✓', text: t('home.ctaSupport') },
              { icon: '✓', text: t('home.ctaGuarantee') },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-light">
                <span className="text-2xl">{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
